import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePrivateMutation } from "@/lib/query/core/use-private-mutation";
import {
  apiJsonClient,
  ApiJsonClientError,
} from "@/lib/query/core/api-json-client";
import { keys } from "@/lib/i18n/portal";
import { apiErrorMessage } from "@/lib/api/error-copy";
import { translateStandalone } from "@/lib/i18n/standalone";
import { isIosCompanionApp, isSellerNativeApp, isSellerNativeAppOrigin } from "@/lib/native/runtime";
import {
  clearNativeMetaAuthorization,
  openNativeMetaAuthorization,
  prepareNativeMetaAuthorization,
  registerNativeMetaAttempt,
  type NativeMetaPurpose,
} from "@/lib/native/meta-authorization";
import { awaitMetaStart } from "./authorization-start";

export type MetaConfig = {
  identityLoginEnabled: boolean;
  identityInstagramLoginEnabled: boolean;
  businessFacebookEnabled: boolean;
  businessInstagramEnabled: boolean;
  combinedFacebookEnabled: boolean;
  combinedInstagramEnabled: boolean;
  mediaImportApproved: boolean;
  liveEnabled: boolean;
};
export type MetaIdentityProvider = "FACEBOOK" | "INSTAGRAM";

export function metaIdentityErrorMessage(
  error: unknown,
  provider: MetaIdentityProvider,
): string {
  if (error instanceof ApiJsonClientError) {
    if (error.status === 503)
      return translateStandalone(
        provider === "INSTAGRAM"
          ? keys.meta.instagramIdentityLoginUnavailable
          : keys.meta.identityLoginUnavailable,
      );
    if (error.code === "META_IDENTITY_NOT_LINKED" && provider === "INSTAGRAM")
      return translateStandalone(keys.meta.errors.instagramIdentityNotLinked);
  }
  return metaErrorMessage(error);
}

export const metaKeys = {
  root: (storeSlug: string) => ["seller", storeSlug, "meta"] as const,
  data: (storeSlug: string, path: string) =>
    ["seller", storeSlug, "meta", path] as const,
};

/** Every Meta write is protected by the server's narrow same-origin guard. */
export async function metaRequest<T>(
  path: string,
  method = "GET",
  body?: unknown,
  options?: { signal: AbortSignal },
): Promise<T> {
  const signal = options?.signal;
  if (signal?.aborted) throw signal.reason ?? new DOMException("Authorization cancelled", "AbortError");
  if (isIosCompanionApp() && method === "POST" && (
    /^\/(?:public\/meta\/auth|seller\/meta\/identity)\/(?:start|complete)$/.test(path) ||
    /^\/seller\/meta\/connections\/[^/]+\/identity$/.test(path) ||
    (path === "/seller/meta/connections/start" &&
      ((body as { enableSignIn?: boolean } | undefined)?.enableSignIn ||
        (body as { signInConsent?: boolean } | undefined)?.signInConsent))
  )) throw new ApiJsonClientError({
    status: 403,
    code: "META_IDENTITY_LOGIN_DISABLED",
    message: translateStandalone(keys.meta.identityLoginUnavailable),
  });
  const native = isSellerNativeApp();
  const nativePurpose: NativeMetaPurpose | undefined = native && method === "POST"
    ? ({ "/public/meta/auth/start": "LOGIN", "/seller/meta/identity/start": "LINK", "/seller/meta/connections/start": "BUSINESS_CONNECT" } as const)[path as "/public/meta/auth/start"]
    : undefined;
  let requestBody = body;
  let nativeRequestId: string | undefined;
  if (nativePurpose) {
    const input = body as { provider: MetaIdentityProvider; locale: "ar" | "en" };
    const pending = await awaitMetaStart(() => prepareNativeMetaAuthorization({ provider: input.provider, locale: input.locale, purpose: nativePurpose }, signal), signal);
    nativeRequestId = pending.requestId;
    requestBody = { ...input, nativeChallenge: pending.challenge };
  }
  try {
    const result = await awaitMetaStart(() => apiJsonClient<T>(path, {
    method,
    ...(signal ? { signal } : {}),
    ...(method === "GET"
      ? {}
      : {
          headers: {
            "Content-Type": "application/json", "X-Trioty-Meta": "1",
            // CapacitorHttp uses a native transport, which does not synthesize
            // browser Origin. Keep the server's exact allowlist unchanged.
            ...(native && isSellerNativeAppOrigin()
              ? { Origin: "https://seller.trioty.com" } : {}),
          },
          ...(requestBody === undefined ? {} : { body: JSON.stringify(requestBody) }),
        }),
    }), signal);
    if (signal?.aborted) throw signal.reason ?? new DOMException("Authorization cancelled", "AbortError");
    if (nativeRequestId) registerNativeMetaAttempt((result as { nativeAttemptId: string }).nativeAttemptId, nativeRequestId);
    return result;
  } catch (error) {
    if (nativeRequestId) clearNativeMetaAuthorization(nativeRequestId);
    throw error;
  }
}

export function useMetaConfig() {
  return useQuery({
    queryKey: ["public-meta-config"],
    queryFn: () => metaRequest<MetaConfig>("/public/meta/config"),
  });
}

export function useMetaQuery<T>(
  storeSlug: string,
  path: string,
  enabled = true,
  refreshOnReturn = false,
) {
  return useQuery({
    queryKey: metaKeys.data(storeSlug, path),
    queryFn: ({ signal }) => metaRequest<T>(path, "GET", undefined, { signal }),
    enabled: enabled && Boolean(storeSlug),
    // The meta revision changes when a background job or connection changes.
    ...(refreshOnReturn ? { retry: false, refetchOnWindowFocus: true, refetchOnMount: "always" as const } : {}),
  });
}

export function useMetaWrite<T, V = void>(
  storeSlug: string,
  operation: (input: V) => Promise<T>,
  options?: { invalidateOnSuccess: boolean },
) {
  const client = useQueryClient();
  return usePrivateMutation({
    // An ambiguous provider effect must never be replayed by a toast's Retry.
    meta: { skipGlobalErrorToast: true },
    retry: false,
    mutationFn: operation,
    // A failed response can follow a committed job; reconcile reads without replaying the write.
    onSettled: async () => {
      if (options?.invalidateOnSuccess !== false)
        await client.invalidateQueries({ queryKey: metaKeys.root(storeSlug) });
    },
  });
}

export function metaErrorMessage(error: unknown): string {
  if (error instanceof ApiJsonClientError && error.code?.startsWith("META_")) {
    return metaCodeMessage(error.code);
  }
  return apiErrorMessage(error);
}

export function metaCodeMessage(code: string): string {
  if (code === "META_CATALOG_PRODUCT_HIDDEN")
    return translateStandalone(keys.meta.catalogHiddenSkipped);
  if (code === "META_PROVIDER_CONFIG_CHANGED")
    return translateStandalone(keys.meta.errors.providerConfigChanged);
  if (code === "META_CATALOG_SYNC_DISABLED")
    return translateStandalone(keys.meta.catalogSyncOff);
  if (code === "META_CONNECTION_IDENTITY_OWNER_REQUIRED")
    return translateStandalone(keys.meta.connectionIdentityOwnerRequired);
  if (code === "META_SIGN_IN_APP_MISMATCH")
    return translateStandalone(keys.meta.unifiedAppMismatch);
  if (code === "META_IDENTITY_ALREADY_LINKED")
    return translateStandalone(keys.meta.unifiedIdentityAlreadyLinked);
  if (code === "META_REAL_SELLER_REQUIRED")
    return translateStandalone(keys.meta.errors.realSellerRequired);
  if (
    code === "META_IDENTITY_PROVIDER_MISMATCH" ||
    code === "META_OAUTH_PROVIDER_MISMATCH"
  )
    return translateStandalone(keys.meta.errors.identityProviderMismatch);
  if (
    code === "META_INSTAGRAM_PROFESSIONAL_REQUIRED" ||
    code === "META_PROFESSIONAL_ACCOUNT_REQUIRED"
  )
    return translateStandalone(keys.meta.errors.instagramProfessionalRequired);
  const validationKeys: Record<string, string> = {
    META_MEDIA_DECODE_INVALID: keys.meta.errors.mediaDecode,
    META_MEDIA_DIMENSIONS_INVALID: keys.meta.errors.imageDimensions,
    META_MEDIA_ENCODING_INVALID: keys.meta.errors.mediaEncoding,
    META_VIDEO_LIMITS_INVALID: keys.meta.errors.videoLimits,
    META_INSTAGRAM_IMAGE_DIMENSIONS_INVALID:
      keys.meta.errors.instagramImageDimensions,
    META_INSTAGRAM_REEL_LIMITS_INVALID: keys.meta.errors.instagramReelLimits,
    META_MEDIA_DECODE_LIMIT_EXCEEDED: keys.meta.errors.mediaDecodeLimit,
    META_MEDIA_VALIDATION_BUSY: keys.meta.errors.mediaValidationBusy,
    META_MEDIA_VALIDATION_UNAVAILABLE:
      keys.meta.errors.mediaValidationUnavailable,
    META_MEDIA_METADATA_REQUIRED: keys.meta.errors.mediaMetadataRequired,
  };
  if (validationKeys[code]) return translateStandalone(validationKeys[code]);
  if (code === "META_IDENTITY_NOT_LINKED")
    return translateStandalone(keys.meta.errors.identityNotLinked);
  if (code === "META_SCHEDULE_MISSED")
    return translateStandalone(keys.meta.scheduleMissed);
  if (code === "META_PUBLISH_ACCOUNT_BUSY")
    return translateStandalone(keys.meta.errors.accountBusy);
  const key = /PASSWORD/.test(code)
    ? keys.meta.errors.password
    : /REVOKED|RECONNECT|TOKEN|CREDENTIAL/.test(code)
      ? keys.meta.errors.reconnect
      : /ORIGINAL|IMAGE_UPLOAD/.test(code)
        ? keys.meta.mediaUnavailable
        : /CONTENT_INVALID|REEL_VIDEO|USE_REEL|SIZE_OR_TYPE|TOO_LARGE|PROCESSING_VALIDATION/.test(
              code,
            )
          ? keys.meta.errors.mediaFormat
          : /ID_UNRESOLVED|ID_RECONCILIATION_REQUIRED|OUTCOME_UNKNOWN|RETRY_UNSAFE/.test(
                code,
              )
            ? keys.meta.reconcilingHint
            : /SCHEDULE|TIMEZONE/.test(code)
              ? keys.meta.scheduleInvalid
              : /POLICY|APPROVAL|APPROVED|PURPOSE/.test(code)
                ? keys.meta.errors.approval
                : /DISABLED|NOT_CONFIGURED|LIVE/.test(code)
                  ? keys.meta.errors.unavailable
                  : /PERMISSION|ASSET|ROLE/.test(code)
                    ? keys.meta.errors.permission
                    : /STATE|TICKET|REPLAY|CALLBACK/.test(code)
                      ? keys.meta.errors.callback
                      : /QUOTA|RATE|BUDGET/.test(code)
                        ? keys.meta.errors.quota
                        : /CONFLICT|STALE|CHANGED|EXPIRED|READBACK/.test(code)
                          ? keys.meta.errors.conflict
                          : /INITIAL_FIELDS|ITEM_VALIDATION|NOT_INGESTED/.test(
                                code,
                              )
                            ? keys.meta.errors.catalogValidation
                            : keys.meta.errors.generic;
  return translateStandalone(key);
}

/** Only navigate to a documented Meta authorization origin, never an arbitrary API value. */
export function validMetaAuthorizationUrl(
  value: string,
  provider?: MetaIdentityProvider,
): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      !url.username &&
      !url.password &&
      !url.port &&
      !url.href.includes("#") &&
      (!provider ||
        (provider === "FACEBOOK"
          ? ["www.facebook.com", "facebook.com"].includes(url.hostname)
          : ["www.instagram.com", "api.instagram.com"].includes(
              url.hostname,
            ))) &&
      ((["www.facebook.com", "facebook.com"].includes(url.hostname) &&
        /^(?:\/v\d+\.\d+)?\/dialog\/oauth\/?$/.test(url.pathname)) ||
        (["www.instagram.com", "api.instagram.com"].includes(url.hostname) &&
          /^\/oauth\/authorize\/?$/.test(url.pathname)))
    );
  } catch {
    return false;
  }
}

export function openMetaAuthorization(
  value: string,
  provider?: MetaIdentityProvider,
  nativeAttemptId?: string,
) {
  if (isSellerNativeApp()) return openNativeMetaAuthorization(value, provider, nativeAttemptId);
  if (!validMetaAuthorizationUrl(value, provider))
    throw new Error("Invalid authorization destination");
  const userAgent = navigator.userAgent;
  if (
    /Android/i.test(userAgent) &&
    /Chrome\//.test(userAgent) &&
    !/; wv\)|SamsungBrowser|EdgA|OPR\/|FBAN|FBAV|Instagram/i.test(userAgent)
  ) {
    const url = new URL(value);
    const packageName = url.hostname.endsWith("facebook.com")
      ? "com.facebook.katana"
      : "com.instagram.android";
    // Chrome keeps the exact OAuth URL in the browser when no app handles it.
    window.location.assign(
      `${url.href.replace(/^https:/, "intent:")}#Intent;scheme=https;package=${packageName};S.browser_fallback_url=${encodeURIComponent(url.href)};end`,
    );
    return;
  }
  window.location.assign(value);
}

/** Display only the provider's persisted URL; never derive a link from a remote ID. */
export function safeMetaPublishedPermalink(
  value: string | null | undefined,
  provider: "FACEBOOK" | "INSTAGRAM",
): string | null {
  if (!value || value.length > 2048) return null;
  try {
    const url = new URL(value);
    const domain = provider === "FACEBOOK" ? "facebook.com" : "instagram.com";
    if (
      url.protocol !== "https:" ||
      url.username ||
      url.password ||
      url.port ||
      !(url.hostname === domain || url.hostname.endsWith(`.${domain}`))
    )
      return null;
    return url.href;
  } catch {
    return null;
  }
}
